import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import PageHero from '../../../components/feature/PageHero';
import SEOHead from '../../../components/feature/SEOHead';
import CountdownTimer from '../../../components/feature/CountdownTimer';

export default function ConsultoriaTecnicaPage() {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Consultoria Técnica em Seguros",
    "provider": {
      "@type": "Organization",
      "name": "IBX Brasil"
    },
    "description": "Consultoria técnica especializada em análise de riscos, dimensionamento de coberturas e adequação de apólices às necessidades empresariais.",
    "serviceType": "Consultoria Técnica em Seguros",
    "areaServed": "BR"
  };

  return (
    <>
      <SEOHead
        title="Consultoria Técnica em Seguros | Análise Especializada"
        description="Consultoria técnica especializada em seguros empresariais. Análise de riscos, dimensionamento de coberturas, adequação de apólices e proteção estratégica para sua empresa."
        canonical="https://ibxbrasil.com.br/servicos/consultoria-tecnica"
        ogImage="https://readdy.ai/api/search-image?query=professional%20insurance%20technical%20consultant%20analyzing%20risk%20assessment%20reports%20and%20coverage%20documents%20modern%20office%20desk%20with%20charts%20graphs%20laptop%20professional%20lighting%20corporate%20atmosphere%20realistic%20photography&width=1200&height=630&seq=seo-consultoria-tecnica-service&orientation=landscape"
        ogType="service"
        schema={serviceSchema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <PageHero
          badge="SERVIÇOS • CONSULTORIA TÉCNICA"
          title="Consultoria Técnica Especializada em Seguros"
          highlightedWord="Consultoria"
          highlightColor="orange"
          subtitle="Análise profunda de riscos, estruturação de programas de seguros corporativos e assessoria estratégica para decisões técnicas complexas."
          benefits={[
            'Diagnóstico completo de riscos',
            'Estruturação de programas corporativos',
            'Análise técnica de cláusulas',
            'Assessoria estratégica contínua'
          ]}
          ctaText="Falar no WhatsApp"
          ctaLink="https://wa.me/5577988080505?text=Olá! Gostaria de saber mais sobre consultoria técnica em seguros."
          ctaSecondary={{
            text: 'Solicitar Análise',
            link: '#contato'
          }}
          backgroundImage="https://readdy.ai/api/search-image?query=professional%20insurance%20technical%20consulting%20meeting%20with%20business%20executives%20in%20modern%20corporate%20office%20strategic%20planning%20documents%20professional%20atmosphere%20elegant%20environment&width=1920&height=700&seq=consultoria-tecnica-hero-001&orientation=landscape"
          overlayOpacity="medium"
        />

        {/* Consulting Areas */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Áreas de Consultoria
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Orientação especializada em gestão de riscos e seguros
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Análise de Riscos Corporativos</h3>
                <p className="text-gray-600 leading-relaxed">
                  Mapeamento completo de riscos operacionais, patrimoniais, financeiros e de responsabilidade civil da empresa.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-file-chart-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Planejamento Estratégico</h3>
                <p className="text-gray-600 leading-relaxed">
                  Estruturação de programa de seguros alinhado aos objetivos de negócio e capacidade de investimento.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-calculator-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Dimensionamento de Coberturas</h3>
                <p className="text-gray-600 leading-relaxed">
                  Cálculo técnico de capitais segurados, limites de indenização e estruturação de coberturas adequadas.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-exchange-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Revisão de Contratos Vigentes</h3>
                <p className="text-gray-600 leading-relaxed">
                  Análise técnica de apólices atuais para identificar falhas de cobertura e oportunidades de melhoria.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-building-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Seguros Empresariais Complexos</h3>
                <p className="text-gray-600 leading-relaxed">
                  Orientação em seguros especializados: D&O, E&O, riscos de engenharia, transportes e responsabilidades.
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-8">
                <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-team-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Capacitação de Equipes</h3>
                <p className="text-gray-600 leading-relaxed">
                  Treinamento de gestores e equipes internas sobre gestão de riscos e utilização adequada de seguros.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* When to Hire */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Quando Contratar Consultoria Técnica?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Situações estratégicas que exigem orientação especializada
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-8 border border-orange-200">
                <div className="flex items-start gap-4">
                  <i className="ri-rocket-line text-3xl text-orange-600 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Expansão de Negócios</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Novos investimentos, abertura de filiais ou mudança de atividade que alteram o perfil de riscos da empresa.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border border-orange-200">
                <div className="flex items-start gap-4">
                  <i className="ri-exchange-funds-line text-3xl text-orange-600 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Fusões e Aquisições</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Processos de M&A que exigem análise de riscos e estruturação de coberturas para novos ativos.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border border-orange-200">
                <div className="flex items-start gap-4">
                  <i className="ri-alert-line text-3xl text-orange-600 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Sinistros Recorrentes</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Alta sinistralidade que impacta custos e exige revisão estratégica de coberturas e gestão de riscos.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border border-orange-200">
                <div className="flex items-start gap-4">
                  <i className="ri-money-dollar-circle-line text-3xl text-orange-600 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Custos Elevados</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Prêmios de seguros representando percentual significativo do orçamento e necessidade de otimização.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border border-orange-200">
                <div className="flex items-start gap-4">
                  <i className="ri-building-4-line text-3xl text-orange-600 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Novos Projetos</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Obras, construções ou projetos especiais que demandam seguros específicos e análise técnica.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 border border-orange-200">
                <div className="flex items-start gap-4">
                  <i className="ri-file-shield-line text-3xl text-orange-600 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Conformidade Regulatória</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      Necessidade de adequação a exigências legais, contratuais ou de órgãos reguladores.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <CountdownTimer location="paginas-servicos" />
            <h2 className="text-4xl font-bold text-white mb-6">
              Transforme Sua Gestão de Seguros Hoje
            </h2>
            <p className="text-xl text-orange-100 mb-8">
              Solicite uma análise gratuita e descubra como proteger melhor seu patrimônio
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/5577988080505"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors duration-300 flex items-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-2xl"></i>
                <span>Falar no WhatsApp</span>
              </a>
              <a
                href="/contato"
                className="inline-flex items-center gap-2 px-8 py-4 bg-strategic-green-500 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-strategic-green-600 hover:scale-105 hover:shadow-xl whitespace-nowrap"
              >
                Solicitar Consultoria
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
              <a
                href="/servicos"
                className="inline-flex items-center justify-center px-6 py-3 bg-action-orange-500 text-white font-semibold rounded-lg hover:bg-action-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Ver Outros Serviços
                <i className="ri-arrow-right-line ml-2"></i>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}